import type { ContentPack, ProvinceConfig, ProvinceSeoOverrides, ProvincePack } from "./_types";
import { PHUKET_CONFIG } from "./phuket/config";
import { PHUKET_CONTENT_CORE, type ProvinceContentCore } from "./phuket/content-core";
import { PHUKET_SEO } from "./phuket/seo";

/** Lightweight pack — heavy ContentPack (case studies, FAQ, guides) is loaded on demand. */
export interface ProvincePackLite {
  config: ProvinceConfig;
  seo: ProvinceSeoOverrides;
  core: ProvinceContentCore;
}

export const PROVINCE_REGISTRY: Record<string, ProvincePackLite> = {
  phuket: { config: PHUKET_CONFIG, seo: PHUKET_SEO, core: PHUKET_CONTENT_CORE },
};

const CONTENT_LOADERS: Record<string, () => Promise<ContentPack>> = {
  phuket: async () => (await import("./phuket/content")).PHUKET_CONTENT,
};

function readEnvSlug(): string {
  const viteEnv =
    typeof import.meta !== "undefined"
      ? (import.meta as unknown as { env?: Record<string, string | undefined> }).env
      : undefined;
  const fromVite = viteEnv?.VITE_PROVINCE_SLUG;
  const fromProcess =
    typeof process !== "undefined" ? process.env?.VITE_PROVINCE_SLUG : undefined;
  return (fromVite || fromProcess || "phuket").toLowerCase();
}

const ACTIVE_SLUG = readEnvSlug();

export function listProvinceSlugs(): string[] {
  return Object.keys(PROVINCE_REGISTRY);
}

export function getProvince(slug: string): ProvincePackLite {
  const pack = PROVINCE_REGISTRY[slug];
  if (!pack) {
    throw new Error(
      `[provinces] unknown slug "${slug}". Registered: ${listProvinceSlugs().join(", ")}`,
    );
  }
  return pack;
}

export function getActiveProvince(): ProvincePackLite {
  return getProvince(ACTIVE_SLUG);
}

/** Heavy content pack — call from route loaders / server handlers only. */
export async function getProvinceContent(slug: string): Promise<ContentPack> {
  const load = CONTENT_LOADERS[slug];
  if (!load) throw new Error(`[provinces] no content loader for "${slug}"`);
  return load();
}

export async function getActiveProvinceContent(): Promise<ContentPack> {
  return getProvinceContent(ACTIVE_SLUG);
}

/** Full pack (config + seo + content) — validation scripts only. */
export async function getFullProvincePack(slug: string): Promise<ProvincePack> {
  const { config, seo } = getProvince(slug);
  return { config, seo, content: await getProvinceContent(slug) };
}